import React, { useEffect, useState } from "react";
import { Badge, Card, Col, Container, Row, Button } from "react-bootstrap";
import { CheckCircleFill, JournalText, PencilFill, TrashFill } from "react-bootstrap-icons";
import Select, { SingleValue } from "react-select";
import { UserService } from "../../../service/users.service";
import { turnoMap, User, CreateUserDto } from "../../../service/types/types";
import UpdateUser from "./updateUser";
import { useParams } from "react-router-dom";
import HojaDeVidaModal from "./hojaDeVida.modal";
import { HojaDeVidaService } from "../../../service/holaDeVida.service";
import ManageUserStatusModal from "./manageUserStatusModal";

export default function ViewUser() {
  const [selectedUser, setSelectedUser] = useState<CreateUserDto | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [usersOptions, setUsersOptions] = useState<{ value: string; label: string }[]>([]);
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUserSelected, setIsUserSelected] = useState(false);
  const [isUserRequested, setIsUserRequested] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if(!isUserRequested || isUpdated) {
      const fetchUsers = async () => {
        try {
          const data = await UserService.findAll();
          setUsers(data);
          const options = data.map((user) => ({
            value: user.rut,
            label: `${user.name} (${user.rut})`,
          }));
          setUsersOptions(options);
          setIsUserRequested(true);
          setIsUpdated(false);
        } catch (error) {
          alert("Error obteniendo trabajadores");
          console.error("Error fetching users:", error);
        }
      };
      fetchUsers();
    }
  }, [isUpdated]);

  useEffect(() => {
    if (id && users.length > 0 && !isUserSelected) {
      handleSelect({ value: id, label: "" });
      setIsUserSelected(true)
    }
    setShowUpdate(false);
  }, [id, users]);

  const handleSelect = (option: SingleValue<{ value: string; label: string }>) => {
    if (!option) {
      setSelectedUser(null);
      setShowUpdate(false);
      return;
    }
    const user = users.find((u) => u.rut === option.value) || null;
    if (user) {
      console.log("Selected user:", user);
      const selected: CreateUserDto = {
        rut: user.rut,
        name: user.name,
        apellido_paterno: user.apellido_paterno,
        apellido_materno: user.apellido_materno,
        fecha_nacimiento: user.fecha_nacimiento,
        sexo: user.sexo,
        comuna: user.comuna,
        provincia: user.provincia,
        region: user.region || "",
        estado_civil: user.estado_civil || "",
        nivel_educacion: user.nivel_educacion || "",
        numero_cargas: user.numero_cargas || 0,
        afp: user.afp || "",
        no_calzado: user.no_calzado || 0,
        talla: user.talla || "",
        fecha_ingreso: user.fecha_ingreso || "",
        sueldo: user.sueldo || 0,
        bonos: user.bonos || 0,
        phone: user.phone || "",
        email: user.email || "",
        direccion: user.direccion || "",
        cargo: user.cargo || "",
        jornada: user.jornada || "",
        contrato: user.contrato || "",
        prevision: user.prevision || "",
        password: user.password || "",
        turno: user.turno || "",
        is_active: user.is_active || false,
      };
      setSelectedUser(selected);
      setShowUpdate(false);
    }
  };

  const openHojaDeVida = () => {
    setIsOpen(true);
  };

  const closeHojaDeVida = () => {
    setIsOpen(false);
  };

  const renderField = (label: string, value?: any) => (
    <Col md={4} className="mb-3" key={label}>
      <div className="p-3 border rounded bg-white">
        <small className="d-block text-uppercase text-muted" style={{ fontSize: "0.7rem", letterSpacing: "0.03em" }}>
          {label}
        </small>
        <span className="fs-6">{value || <span className="text-muted">—</span>}</span>
      </div>
    </Col>
  );

  const renderSection = (title: string, fields: [string, any][]) => (
    <div className="mb-4" key={title}>
      <h6 className="text-uppercase text-secondary small fw-bold mb-2">{title}</h6>
      <Row>{fields.map(([label, value]) => renderField(label, value))}</Row>
    </div>
  );

  const disableUser = async (fecha: string, descripcion: string) => {
    if(selectedUser){
      await HojaDeVidaService.create({
        user_rut: selectedUser.rut,
        fecha: fecha,
        texto: descripcion,
      });
      await UserService.remove(selectedUser.rut)
        .then(() => {
          alert("Usuario deshabilitado correctamente");
          setSelectedUser(null);
          setShowDelete(false);
        })
        .catch((error) => {
          console.error("Error deshabilitando usuario:", error);
          alert("Error deshabilitando usuario");
        });
      setIsUpdated(true);
    }
  }
  const enableUser = async (fecha: string, descripcion: string) => {
    if(selectedUser){
      await HojaDeVidaService.create({
        user_rut: selectedUser.rut,
        fecha: fecha,
        texto: descripcion,
      });
      await UserService.enable(selectedUser.rut)
        .then(() => {
          alert("Usuario habilitado correctamente");
          setSelectedUser(null);
          setShowDelete(false);
        })
        .catch((error) => {
          console.error("Error habilitando usuario:", error);
          alert("Error deshabilitando usuario");
        });
      setIsUpdated(true);
    }
  }
  const refreshUser = async () => {
    setShowUpdate(false);
    setIsUserSelected(false);
    setIsUpdated(true);
  }
  
  return (
    <div className="w-100 min-vh-100 bg-light d-flex justify-content-center p-3">
      <Container fluid>
        <h2 className="mb-4 text-center">Información de Usuarios</h2>

        {/* Buscador */}
        <Card className="shadow-sm mb-4 p-3">
          <Select
            options={usersOptions}
            onChange={handleSelect}
            placeholder="Buscar usuario por nombre o RUT..."
            isSearchable
            className="w-100"
          />
        </Card>

        {/* Información del usuario seleccionado */}
        
        {selectedUser && !showUpdate && (
          <Card className={`shadow-sm p-4 ${selectedUser.is_active === false ? "border-danger-subtle bg-danger-subtle" : ""}`}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h4 className="mb-0">
                {selectedUser.name} <span className="text-muted fs-6">({selectedUser.rut})</span>
              </h4>
              <Badge bg={selectedUser.is_active === false ? "danger" : "success"}>
                {selectedUser.is_active === false ? "Deshabilitado" : "Activo"}
              </Badge>
            </div>

            {renderSection("Identificación", [
              ["RUT", selectedUser.rut],
              ["Nombre", selectedUser.name],
              ["Apellido Paterno", selectedUser.apellido_paterno],
              ["Apellido Materno", selectedUser.apellido_materno],
              ["Fecha Nacimiento", selectedUser.fecha_nacimiento],
              ["Sexo", selectedUser.sexo],
            ])}

            {renderSection("Contacto", [
              ["Teléfono", selectedUser.phone],
              ["Email", selectedUser.email],
              ["Dirección", selectedUser.direccion],
              ["Comuna", selectedUser.comuna],
              ["Provincia", selectedUser.provincia],
              ["Región", selectedUser.region],
            ])}

            {renderSection("Datos Laborales", [
              ["Cargo", selectedUser.cargo],
              ["Jornada", selectedUser.jornada],
              ["Contrato", selectedUser.contrato],
              ["Previsión", selectedUser.prevision],
              ["Turno", turnoMap[selectedUser.turno] ?? selectedUser.turno],
              ["Fecha Ingreso", selectedUser.fecha_ingreso],
            ])}

            {renderSection("Otros Datos", [
              ["Estado Civil", selectedUser.estado_civil],
              ["Nivel Educación", selectedUser.nivel_educacion],
              ["Número de Cargas", selectedUser.numero_cargas],
              ["AFP", selectedUser.afp],
              ["N° Calzado", selectedUser.no_calzado],
              ["Talla", selectedUser.talla],
              ["Sueldo", selectedUser.sueldo],
              ["Bonos", selectedUser.bonos],
            ])}

            <div className="d-flex flex-wrap gap-2 justify-content-center pt-3 border-top">
              <Button variant="outline-primary" onClick={() => setShowUpdate(true)}>
                <PencilFill className="me-2" />
                Editar Usuario
              </Button>
              <Button variant="outline-secondary" onClick={openHojaDeVida}>
                <JournalText className="me-2" />
                Ver Hoja de Vida
              </Button>
              {selectedUser.is_active === true && (
                <Button variant="outline-danger" onClick={() => setShowDelete(true)}>
                  <TrashFill className="me-2" />
                  Eliminar Usuario
                </Button>
              )}
              {selectedUser.is_active === false && (
                <Button variant="outline-success" onClick={() => setShowDelete(true)}>
                  <CheckCircleFill className="me-2" />
                  Habilitar Usuario
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Componente de edición */}
        {selectedUser && showUpdate && (
          <UpdateUser user={selectedUser} refreshUser={refreshUser} />
        )}
      </Container>

      <ManageUserStatusModal 
        show={showDelete} 
        close={() => setShowDelete(false)}
        isDisable={selectedUser?.is_active === false ? false : true}  
        disableUser={disableUser}
        enableUser={enableUser}/>

      <HojaDeVidaModal
        isOpen={isOpen}
        onRequestClose={closeHojaDeVida}
        userId={selectedUser?.rut || ""}
      />
    </div>
  );
}